export type FanSignupSource = 'backstage' | 'website-vip';

export interface FanSignup {
  id: string;
  firstName: string;
  email: string;
  phone?: string;
  smsOptIn: boolean;
  source: FanSignupSource;
  captureSourceId?: string;
  createdAt: string;
  updatedAt: string;
  tags: string[];
  notes: string;
}

export type FanSignupInput = Pick<FanSignup, 'firstName' | 'email' | 'phone' | 'smsOptIn' | 'source' | 'captureSourceId'>;

export const fanSignupsStorageKey = 'artistos-fan-signups';
export const fanSignupsUpdatedEvent = 'artistos:fan-signups-updated';

export function getFanSignups(): FanSignup[] {
  try {
    const stored = window.localStorage.getItem(fanSignupsStorageKey);
    const signups = stored ? JSON.parse(stored) : [];
    return Array.isArray(signups) ? signups.map(normalizeFanSignup) : [];
  } catch {
    return [];
  }
}

export function addFanSignup(signup: FanSignupInput): boolean {
  try {
    const existing = getFanSignups();
    const normalizedEmail = signup.email.trim().toLowerCase();
    const now = new Date().toISOString();
    const record: FanSignup = {
      ...signup,
      email: normalizedEmail,
      id: crypto.randomUUID(),
      createdAt: now,
      updatedAt: now,
      tags: [signup.source === 'website-vip' ? 'VIP' : 'Backstage'],
      notes: '',
    };
    const duplicateIndex = existing.findIndex((fan) => fan.email === normalizedEmail);
    const updated = duplicateIndex === -1
      ? [record, ...existing]
      : existing.map((fan, index) => index === duplicateIndex ? { ...fan, ...record, id: fan.id, createdAt: fan.createdAt, tags: fan.tags, notes: fan.notes } : fan);
    saveFanSignups(updated);
    void sendSignupToCloud(record);
    return true;
  } catch {
    return false;
  }
}

/**
 * A signup saved only in the fan's own browser never reaches the band, so every
 * submission is also written to the shared database. The local copy stays as an
 * immediate confirmation and as a fallback if the network drops mid-submit.
 */
async function sendSignupToCloud(record: FanSignup): Promise<void> {
  try {
    const { supabase, isCloudConfigured } = await import('./supabase');
    if (!isCloudConfigured()) return;
    await supabase.from('fan_signups').insert({
      name: record.firstName,
      email: record.email,
      phone: record.phone ?? '',
      sms_consent: record.smsOptIn,
      source: record.source,
    });
  } catch {
    // The local copy remains; nothing to show the fan.
  }
}

/**
 * Loads the real fan list from the database and merges it into the local cache,
 * so Fans & VIP shows signups from every device rather than just this browser.
 */
export async function refreshFanSignupsFromCloud(): Promise<{ count: number; error?: string }> {
  try {
    const { supabase, isCloudConfigured } = await import('./supabase');
    if (!isCloudConfigured()) return { count: 0 };

    const { data, error } = await supabase
      .from('fan_signups')
      .select('id, name, email, phone, sms_consent, source, created_at')
      .order('created_at', { ascending: false });

    if (error) throw error;

    const local = getFanSignups();
    const byEmail = new Map(local.map((fan) => [fan.email, fan]));

    (data ?? []).forEach((row) => {
      const email = String(row.email ?? '').trim().toLowerCase();
      if (!email) return;
      const existing = byEmail.get(email);
      const source: FanSignupSource = row.source === 'backstage' ? 'backstage' : 'website-vip';
      byEmail.set(email, {
        id: existing?.id ?? String(row.id),
        firstName: String(row.name ?? existing?.firstName ?? ''),
        email,
        phone: String(row.phone ?? existing?.phone ?? ''),
        smsOptIn: Boolean(row.sms_consent),
        source,
        captureSourceId: existing?.captureSourceId,
        createdAt: String(row.created_at ?? existing?.createdAt ?? new Date().toISOString()),
        updatedAt: existing?.updatedAt ?? String(row.created_at ?? new Date().toISOString()),
        // Tags and notes are yours, kept on this device rather than in the shared list.
        tags: existing?.tags ?? [source === 'website-vip' ? 'VIP' : 'Backstage'],
        notes: existing?.notes ?? '',
      });
    });

    const merged = [...byEmail.values()].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    saveFanSignups(merged);
    return { count: merged.length };
  } catch (error) {
    const raw = error instanceof Error ? error.message : String(error ?? '');
    if (/row-level security|permission|denied|JWT/i.test(raw)) {
      return { count: 0, error: 'Sign in as an owner to see the shared fan list.' };
    }
    return { count: 0, error: 'Could not reach the fan list in the cloud. Showing this device’s copy.' };
  }
}

export function updateFanSignup(id: string, changes: Pick<FanSignup, 'tags' | 'notes'>): boolean {
  try {
    const updated = getFanSignups().map((fan) => fan.id === id ? { ...fan, ...changes, updatedAt: new Date().toISOString() } : fan);
    saveFanSignups(updated);
    return true;
  } catch {
    return false;
  }
}

export function downloadFanSignupsCsv(fans: FanSignup[]) {
  const headers = ['First name', 'Email', 'Phone', 'SMS opted in', 'Source', 'Tags', 'Joined at', 'Updated at', 'Notes'];
  const rows = fans.map((fan) => [fan.firstName, fan.email, fan.phone ?? '', fan.smsOptIn ? 'Yes' : 'No', fan.source, fan.tags.join('; '), fan.createdAt, fan.updatedAt, fan.notes]);
  const csv = [headers, ...rows].map((row) => row.map(escapeCsv).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `artistos-fans-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function normalizeFanSignup(value: unknown): FanSignup {
  const fan = value as Partial<FanSignup>;
  return {
    id: fan.id ?? crypto.randomUUID(),
    firstName: fan.firstName ?? '',
    email: fan.email?.trim().toLowerCase() ?? '',
    phone: fan.phone,
    smsOptIn: Boolean(fan.smsOptIn),
    source: fan.source === 'website-vip' ? 'website-vip' : 'backstage',
    captureSourceId: typeof fan.captureSourceId === 'string' ? fan.captureSourceId : undefined,
    createdAt: fan.createdAt ?? new Date().toISOString(),
    updatedAt: fan.updatedAt ?? fan.createdAt ?? new Date().toISOString(),
    tags: Array.isArray(fan.tags) ? fan.tags.filter((tag): tag is string => typeof tag === 'string') : [fan.source === 'website-vip' ? 'VIP' : 'Backstage'],
    notes: typeof fan.notes === 'string' ? fan.notes : '',
  };
}

function saveFanSignups(signups: FanSignup[]) {
  window.localStorage.setItem(fanSignupsStorageKey, JSON.stringify(signups));
  window.dispatchEvent(new CustomEvent(fanSignupsUpdatedEvent, { detail: signups }));
}

function escapeCsv(value: string) {
  return `"${value.replace(/"/g, '""')}"`;
}
